import { motion } from "framer-motion";
import { Camera, Upload, X, Plus, Heart, History, MessageCircle, Sparkles } from "lucide-react";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const HomeScreen = ({
  uploadedImages,
  setUploadedImages,
  getRootProps,
  getInputProps,
  isDragActive,
  onDrop,
  analyzeImages,
  handleManualEntry,
  openFeedback,
  savedRecipes,
  setShowSaved,
  recipeHistory,
  setShowHistory,
  removeUploadedImage,
}) => {
  return (
    <motion.div
      key="home"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="h-screen flex flex-col overflow-hidden"
      data-testid="home-screen"
    >
      {/* Design image */}
      <div className="flex-1 relative min-h-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_recipe-snap-11/artifacts/bxwbuit5_MoodFoofz_crop.png"
          alt="MoodFood"
          className="w-full h-full object-cover object-top"
        />
        
        {/* Action buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={openFeedback}
            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center border-2 border-[#264653]/10 hover:border-[#2A9D8F]"
            data-testid="header-feedback-btn"
            title="Give Feedback"
          >
            <MessageCircle className="w-4 h-4 text-[#2A9D8F]" />
          </button>
          {savedRecipes.length > 0 && (
            <button
              onClick={() => setShowSaved(true)}
              className="relative w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center border-2 border-[#264653]/10 hover:border-[#E76F51]"
              data-testid="view-saved-btn"
            >
              <Heart className="w-4 h-4 text-[#E76F51] fill-[#E76F51]" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#E76F51] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {savedRecipes.length}
              </span>
            </button>
          )}
          {recipeHistory.length > 0 && (
            <button
              onClick={() => setShowHistory(true)}
              className="relative w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center border-2 border-[#264653]/10 hover:border-[#2A9D8F]"
              data-testid="view-history-btn"
            >
              <History className="w-4 h-4 text-[#2A9D8F]" />
            </button>
          )}
        </div>
      </div>
      
      {/* Bottom section */}
      <div className="bg-[#FEFAE0] px-4 py-4 flex-shrink-0">
        {/* Uploaded Images Preview */}
        {uploadedImages.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#264653]">
                {uploadedImages.length}/4 images selected
              </span>
              <button
                onClick={() => {
                  uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
                  setUploadedImages([]);
                }}
                className="text-xs text-[#E76F51] hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {uploadedImages.map((img, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#264653]/10">
                  <img src={img.preview} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={(e) => { e.stopPropagation(); removeUploadedImage(index); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#E76F51] text-white flex items-center justify-center hover:bg-[#E76F51]/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {uploadedImages.length < 4 && (
                <div
                  {...getRootProps()}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#264653]/20 flex items-center justify-center cursor-pointer hover:border-[#FFB703] hover:bg-[#FFB703]/5 transition-all"
                >
                  <input {...getInputProps()} capture="environment" />
                  <Plus className="w-5 h-5 text-[#264653]/40" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Area */}
        {uploadedImages.length === 0 && (
          <div
            {...getRootProps()}
            className={`relative border-2 border-dashed rounded-2xl p-3 text-center cursor-pointer transition-all duration-300 ${
              isDragActive 
                ? "border-[#FFB703] bg-[#FFB703]/10" 
                : "border-[#264653]/20 bg-white/50 hover:border-[#FFB703]/50"
            }`}
            data-testid="upload-dropzone"
          >
            <input {...getInputProps()} capture="environment" data-testid="file-input" />
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-[#FFB703]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-[#FFB703]" />
              </div>
              <div className="text-left">
                <p className="font-bold text-[#264653] text-sm">Snap your fridge</p>
                <p className="text-[#264653]/60 text-xs">Upload up to 4 images</p>
              </div>
            </div>
          </div>
        )}
        
        {uploadedImages.length > 0 ? (
          <button
            onClick={analyzeImages}
            className="w-full mt-3 btn-primary flex items-center justify-center gap-2 py-3"
            data-testid="scan-images-btn"
          >
            <Sparkles className="w-5 h-5" />
            Scan {uploadedImages.length} Image{uploadedImages.length > 1 ? 's' : ''}
          </button>
        ) : (
          <>
            <input 
              type="file" 
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  onDrop(files.slice(0, 4 - uploadedImages.length));
                }
                e.target.value = '';
              }}
              className="hidden"
              data-testid="gallery-input"
            />
            <button
              onClick={() => document.querySelector('[data-testid="gallery-input"]')?.click()}
              className="w-full mt-3 btn-primary flex items-center justify-center gap-2 py-3"
              data-testid="upload-photos-btn"
            >
              <Upload className="w-5 h-5" />
              Upload Photos
            </button>
            
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-[#264653]/20"></div>
              <span className="text-[#264653]/50 text-xs font-medium">or</span>
              <div className="flex-1 h-px bg-[#264653]/20"></div>
            </div>
            
            <button
              onClick={handleManualEntry}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3"
              data-testid="manual-entry-btn"
            >
              <Plus className="w-5 h-5" />
              Add Ingredients Manually
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};
